
import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavItem } from './SidebarData';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SidebarLogoutItem } from './SidebarLogoutItem';
import { Lock, Crown } from 'lucide-react';
import { useEnhancedSubscription } from '@/hooks/useEnhancedSubscription';
import { MobileBadge } from '@/components/ui/mobile-badge';

interface SidebarNavItemProps {
  item: NavItem;
}

export function SidebarNavItem({ item }: SidebarNavItemProps) {
  const location = useLocation();
  const { state } = useSidebar();
  const { subscription } = useEnhancedSubscription();
  const isActive = location.pathname === item.path;
  const isCollapsed = state === 'collapsed';
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Check if text is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (labelRef.current) {
        setIsTruncated(labelRef.current.scrollWidth > labelRef.current.clientWidth);
      }
    };
    
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [item.label]);

  // Handle logout item specially
  if (item.isLogout) {
    return <SidebarLogoutItem />;
  }

  // Check if this is a pro feature and if user has access
  const isProFeature = item.isProFeature || item.isPro;
  const currentTier = subscription?.tier || 'freemium';
  const isLocked = isProFeature && currentTier === 'freemium';
  
  // Check if this is a mobile-optimized feature
  const isMobileOptimized = item.category === 'mobile_suite';

  const menuButton = (
    <SidebarMenuButton asChild isActive={isActive}>
      <Link to={item.path} className="flex items-center gap-3">
        <item.icon className="h-4 w-4 flex-shrink-0" />
        <span ref={labelRef} className="flex-1 truncate">{item.label}</span>
        {isMobileOptimized && !isCollapsed && <MobileBadge />}
        {isLocked && !isCollapsed && (
          <Lock className="h-3 w-3 text-muted-foreground ml-auto" />
        )}
      </Link>
    </SidebarMenuButton>
  );

  // Enhanced tooltip content with full name and status
  const tooltipContent = (
    <div className="space-y-1 max-w-[200px]">
      <p className="font-medium">{item.fullName}</p>
      {isLocked && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Crown className="h-3 w-3 text-amber-500" /> Pro feature
        </p>
      )}
      {isMobileOptimized && (
        <p className="text-xs text-cyan-500">Mobile optimized</p>
      )}
    </div>
  );

  return (
    <SidebarMenuItem>
      <TooltipProvider>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            {menuButton}
          </TooltipTrigger>
          <TooltipContent 
            side="right" 
            align="center" 
            className="z-[100]"
          >
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </SidebarMenuItem>
  );
}
